import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { RootState } from '@stores/store';
import { GenericTable } from '@components';
import { useLazyDownloadActiveCohortsQuery } from '@api/commissions';
import { ActiveCohortItem } from '@api/types/commissions';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import ActiveCohortSidePanel from './ActiveCohortSidePanel';
import { Column } from '../../../../../components/GenericTable/GenericTable';
import Pagination from '../../Pagination';

interface ActiveCohortProps {
  data: ActiveCohortItem[];
  isLoading: boolean;
  fetchActiveCohorts: (inputProps: { pageNumber?: number; pageSize?: number }) => void;
  page: number;
  size: number;
  total: number;
  handleMarkAsDefaulted: () => void;
  filteredReferrer: string;
}

const ActiveCohorts = ({
  data,
  isLoading,
  fetchActiveCohorts,
  page,
  size,
  total,
  handleMarkAsDefaulted,
  filteredReferrer,
}: ActiveCohortProps) => {
  const [showActiveCohortSidePanel, setShowActiveCohortSidePanel] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const userRole = useSelector((state: RootState) => state.UserData.user.role);

  const dispatch = useDispatch();
  const [downloadActiveCohorts] = useLazyDownloadActiveCohortsQuery();

  const handleRefresh = () => {
    handleMarkAsDefaulted();
  };

  const handleDownload = async () => {
    try {
      const params = {
        referrerId: filteredReferrer === 'All' ? undefined : filteredReferrer,
      };

      const { data } = await downloadActiveCohorts(params).unwrap();
      const url = data?.url;

      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'active-cohorts.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Unable to download CSV, please try again later.',
        }),
      );
    }
  };

  const columns: Column<ActiveCohortItem>[] = [
    {
      key: 'fundingMonth',
      label: 'Funding Month',
    },
    ...(userRole === 'ADMIN'
      ? [
          {
            key: 'referrer' as keyof ActiveCohortItem,
            label: 'Referrer',
          },
        ]
      : []),
    {
      key: 'dealName',
      label: 'Deal Name',
    },
    {
      key: 'grossFundingAmount',
      label: 'Gross Funding Amount',
    },
    {
      key: 'grossRecoupAmount',
      label: 'Gross Recoup Amount',
    },
    {
      key: 'endExposure',
      label: 'End Exposure',
    },
    {
      key: 'irr',
      label: 'IRR',
    },
    {
      key: 'moic',
      label: 'MOIC',
    },
  ];
  const meta = {
    columns: [
      { id: 'fundingMonth', name: 'Funding Month', type: 'string' },
      { id: 'referrer', name: 'Referrer', type: 'string' },
      { id: 'dealName', name: 'Deal Name', type: 'string' },
      {
        id: 'grossFundingAmount',
        name: 'Gross Funding Amount',
        type: 'currency',
        currencyFormat: { currency: 'dollar', scale: 2 },
      },
      {
        id: 'grossRecoupAmount',
        name: 'Gross Recoup Amount',
        type: 'currency',
        currencyFormat: { currency: 'dollar', scale: 2 },
      },
      {
        id: 'endExposure',
        name: 'End Exposure',
        type: 'currency',
        currencyFormat: { currency: 'dollar', scale: 2 },
      },
      { id: 'irr', name: 'IRR', type: 'percentage' },
      { id: 'moic', name: 'MOIC', type: 'percentage', scale: 2 },
    ],
  };

  return (
    <div>
      <Box
        sx={{
          width: '100%',
          height: 'fit-content',
          backgroundColor: '#FFF',
          borderRadius: '0.75rem',
          padding: '0.75rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              color: '#162936',
              lineHeight: '1.375rem',
            }}
          >
            Active Cohorts
          </Typography>

          <Stack direction="row" gap="1rem" alignItems="center">
            <IconButton
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={handleDownload}
              sx={{
                color: '#162C36',
                padding: '4px',
              }}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>

            <Pagination
              page={page + 1}
              size={size}
              total={total}
              handleChange={(page) =>
                fetchActiveCohorts({
                  pageNumber: page - 1,
                })
              }
              fileName={''}
              chartId={''}
              submoduleId={''}
              filterSelection={{}}
            />
          </Stack>
        </Stack>
        <GenericTable
          data={data.map((activeCohort, index) => ({
            ...activeCohort,
            id: index.toString(),
          }))}
          columns={columns}
          meta={meta}
          isLoading={isLoading}
          actions={[
            {
              label: userRole === 'ADMIN' ? 'Edit' : 'Details',
              action: (rowId: string) => {
                setSelectedRowData(data.find((d, index) => index.toString() === rowId));
                setShowActiveCohortSidePanel(true);
              },
            },
          ]}
        />
      </Box>
      {selectedRowData && (
        <ActiveCohortSidePanel
          showActiveCohortSidePanel={showActiveCohortSidePanel}
          setShowActiveCohortSidePanel={setShowActiveCohortSidePanel}
          selectedRowData={selectedRowData}
          refresh={handleRefresh}
        />
      )}
    </div>
  );
};

export default ActiveCohorts;
